import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PromotionStateRow {
  id: string;
  shop_id: string;
  component: string;
  condition_type: string;
  current_stage: string;
  previous_stage: string | null;
  recommended_action: 'promote' | 'hold' | 'demote' | 'hard_demote';
  promotion_score: number;
  regression_score: number;
  stability_score: number;
  cross_tech_consistency: number | null;
  cross_ticket_consistency: number | null;
  last_evaluated_at: string;
  last_stage_change_at: string | null;
  reason: any;
}

export function useComponentPromotionState(shopId: string | null) {
  return useQuery({
    queryKey: ['dvi-component-promotion-state', shopId],
    queryFn: async (): Promise<PromotionStateRow[]> => {
      const { data, error } = await supabase
        .from('dvi_component_promotion_state' as any)
        .select('*')
        .eq('shop_id', shopId!)
        .order('promotion_score', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as PromotionStateRow[];
    },
    enabled: !!shopId,
    staleTime: 30_000,
  });
}

export function useComponentPromotionRow(
  shopId: string | null,
  component: string | null,
  condition: string | null,
) {
  return useQuery({
    queryKey: ['dvi-component-promotion-row', shopId, component, condition],
    queryFn: async (): Promise<PromotionStateRow | null> => {
      const { data, error } = await supabase
        .from('dvi_component_promotion_state' as any)
        .select('*')
        .eq('shop_id', shopId!)
        .eq('component', component!)
        .eq('condition_type', condition!)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as PromotionStateRow) ?? null;
    },
    enabled: !!shopId && !!component && !!condition,
    staleTime: 30_000,
  });
}
